import axios from "axios";
import qs from "qs";
import { appConfig } from "../config/runtime-config";

const GURU_URL = appConfig.guruUrl;
const ACCOUNTS_URL = appConfig.accountsServiceUrl;
const HEADER_PROJECT_ID = "X-PROJECT-ID";

// Module-level token holders -- UserContext wires these on mount
let tokenGetter = null;
let tokenRefresher = null;
let loginRedirector = null;
let refreshPromise = null;

export function setTokenHandlers(getToken, refreshToken, redirectToLogin) {
  tokenGetter = getToken;
  tokenRefresher = refreshToken;
  loginRedirector = redirectToLogin;
}

// --- Axios instances ---
const guruApi = axios.create({ baseURL: GURU_URL });
const accountsApi = axios.create({ baseURL: ACCOUNTS_URL });

// Apply shared interceptors to both instances
[guruApi, accountsApi].forEach((instance) => {
  // Request interceptor: attach Bearer token
  instance.interceptors.request.use((config) => {
    const token = tokenGetter ? tokenGetter() : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor: 401 -> silent refresh -> retry or redirect to login
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retried) {
        originalRequest._retried = true;

        if (tokenRefresher) {
          try {
            if (!refreshPromise) {
              refreshPromise = tokenRefresher().finally(() => {
                refreshPromise = null;
              });
            }
            const newToken = await refreshPromise;
            if (newToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch {
            // Refresh failed -- fall through to redirect
          }
        }

        // Refresh failed or no refresher -- redirect to auth-service login
        if (loginRedirector) {
          loginRedirector();
        }
        return new Promise(() => {}); // Prevent further .catch handling during redirect
      }

      return Promise.reject(error);
    },
  );
});

// Project ID interceptor (guru only)
let projectInterceptorId = null;

export const setupProjectInterceptor = (projectId) => {
  if (projectInterceptorId !== null) {
    guruApi.interceptors.request.eject(projectInterceptorId);
  }

  projectInterceptorId = guruApi.interceptors.request.use((config) => {
    config.headers[HEADER_PROJECT_ID] = projectId;
    return config;
  });
};

/** @type {GuruApiWrapper} Legacy-compatible wrapper around guruApi */
const api = {
  get: (url, params, headers = {}) =>
    guruApi.get(url, {
      params,
      headers,
      paramsSerializer: (p) => qs.stringify(p),
    }),

  post: (url, body, headers = {}) =>
    guruApi.post(url, body, {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }),

  put: (url, body, headers = {}) =>
    guruApi.put(url, body, {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }),

  delete: (url, headers = {}) =>
    guruApi.delete(url, {
      headers,
    }),
};

export { accountsApi, api, guruApi };
